import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const HERO = "/home-hero.jpg";

export const AuthShell = ({ title, subtitle, children }) => (
  <div className="relative min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#0B192C] lg:bg-white">
    <img src={HERO} alt="Nacra 17 Italia in regata" data-testid="auth-bg-mobile" className="lg:hidden fixed inset-0 w-full h-full object-cover object-[22%_center]" />
    <div className="lg:hidden fixed inset-0 bg-[#0B192C]/40" />
    <div className="relative hidden lg:block overflow-hidden bg-[#0B192C] grain">
      <img src={HERO} alt="Nacra 17 Italia in regata" data-testid="auth-hero-image" className="absolute inset-0 w-full h-full object-cover object-[22%_center]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/90 via-[#0B192C]/10 to-transparent" />
      <div className="absolute bottom-0 left-0 p-12 text-white max-w-xl">
        <div className="tricolore h-1 w-24 mb-6" />
        <h2 className="font-heading text-5xl font-bold uppercase leading-[0.95]">29er · 49er · 49er FX<br />Nacra 15 · Nacra 17</h2>
        <p className="mt-4 text-slate-200">Libro soci, tessera, flotta, calendario regate e mercatino della classe.</p>
      </div>
    </div>
    <div className="relative flex items-center justify-center px-4 sm:px-6 py-10 lg:py-12">
      <div className="w-full max-w-md fade-up bg-white/95 backdrop-blur-md rounded-2xl p-6 sm:p-8 shadow-2xl lg:bg-transparent lg:shadow-none lg:p-0 lg:backdrop-blur-none">
        <img src="/logo-9er.png" alt="Logo 9er" className="h-20 w-20 mb-6" />
        <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase text-[#0B192C]">{title}</h1>
        <p className="text-muted-foreground mt-2 mb-8">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

const MfaStep = ({ mfa, onBack }) => {
  const { verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [ricorda, setRicorda] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyMfa(mfa.challenge_id, code, ricorda);
      navigate("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/login/resend", { challenge_id: mfa.challenge_id });
      toast.success("Nuovo codice inviato");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="mfa-form">
      <p className="text-sm text-muted-foreground" data-testid="mfa-info">Abbiamo inviato un codice di 6 cifre a <strong className="text-foreground">{mfa.email}</strong>. Inseriscilo per completare l'accesso.</p>
      <div><Label>Codice di accesso</Label><Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" autoFocus required className="text-center text-2xl tracking-[0.5em] h-12" data-testid="mfa-code-input" /></div>
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={ricorda} onCheckedChange={(v) => setRicorda(!!v)} data-testid="mfa-remember-checkbox" />Non chiedere più il codice su questo dispositivo per 30 giorni</label>
      <Button type="submit" disabled={loading || code.length !== 6} className="w-full h-11 bg-[#0B192C] hover:bg-[#1E3E62] transition-colors" data-testid="mfa-submit-button">{loading ? "Verifica…" : "Conferma"}</Button>
      <div className="flex justify-between text-sm">
        <button type="button" onClick={onBack} className="text-muted-foreground hover:underline" data-testid="mfa-back-button">Indietro</button>
        <button type="button" onClick={resend} className="text-emerald-700 font-medium hover:underline" data-testid="mfa-resend-button">Invia un nuovo codice</button>
      </div>
    </form>
  );
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [mfa, setMfa] = useState(null);

  const resend = async () => {
    await api.post("/auth/resend-verification", { username });
    toast.success("Se l'account esiste e non è verificato, riceverai un nuovo link.");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverified(false);
    try {
      const data = await login(username, password);
      if (data.mfa_required) setMfa(data);
      else navigate("/");
    } catch (err) {
      if (err?.response?.status === 403) setUnverified(true);
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Accedi" subtitle="Area riservata ai soci registrati dell'Associazione italiana Classi Nacra-9er.">
      {mfa ? <MfaStep mfa={mfa} onBack={() => setMfa(null)} /> : (
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div><Label>Login (nome utente o email)</Label><Input value={username} onChange={(e) => setUsername(e.target.value)} required autoComplete="username" data-testid="login-username-input" /></div>
        <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" data-testid="login-password-input" /></div>
        <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0B192C] hover:bg-[#1E3E62] transition-colors" data-testid="login-submit-button">
          {loading ? "Accesso…" : "Entra"}
        </Button>
        {unverified && (
          <button type="button" onClick={resend} className="text-sm text-amber-700 font-medium hover:underline" data-testid="login-resend-verification-button">
            Non hai ricevuto l'email di verifica? Inviane una nuova
          </button>
        )}
        <div className="flex flex-wrap justify-between gap-2 text-sm text-muted-foreground">
          <span>Non hai un account? <Link to="/registrazione" className="text-emerald-700 font-medium hover:underline" data-testid="go-to-register-link">Registrati</Link></span>
          <Link to="/password-dimenticata" className="text-emerald-700 font-medium hover:underline" data-testid="forgot-password-link">Password dimenticata?</Link>
        </div>
      </form>
      )}
    </AuthShell>
  );
}
